import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Container, Header, Icon, Label, Modal, Segment, Statistic, Table } from 'semantic-ui-react';
import { useAuth } from '../context/AuthContext';
import { useBooking } from '../context/BookingContext';

const STATUS_META = {
  Pending:   { color: 'yellow', icon: 'clock outline' },
  Approved:  { color: 'green',  icon: 'check circle outline' },
  Rejected:  { color: 'red',    icon: 'times circle outline' },
  Cancelled: { color: 'grey',   icon: 'ban' },
};

function StatusLabel({ status }) {
  const meta = STATUS_META[status] || STATUS_META.Pending;
  return (
    <Label color={meta.color}>
      <Icon name={meta.icon} />{status}
    </Label>
  );
}

function nightsBetween(a, b) {
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function RoomAllocationBadge({ booking }) {
  const assignments = booking.roomAssignments?.filter(a => a.roomNumber) || [];
  const rooms = assignments.length > 0
    ? assignments
    : booking.roomNumber
      ? [{ roomNumber: booking.roomNumber, roomType: booking.roomType }]
      : [];

  if (rooms.length === 0) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: 4 }}>
        <Icon name="key" />Room Allocated
      </div>
      <Label.Group>
        {rooms.map((r, i) => (
          <Label key={i} basic>
            <Icon name="door open" />
            Room {r.roomNumber}
            {r.roomType && ` · ${r.roomType}`}
          </Label>
        ))}
      </Label.Group>
    </div>
  );
}

function BookingCard({ booking, onCancel, onViewDetail }) {
  const nights = nightsBetween(booking.checkIn, booking.checkOut);
  const canCancel = booking.status === 'Pending' || booking.status === 'Approved';
  const statusColor = (STATUS_META[booking.status] || STATUS_META.Pending).color;

  return (
    <Card color={statusColor}>
      <Card.Content>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Card.Header>{booking.bookingCode}</Card.Header>
          <StatusLabel status={booking.status} />
        </div>
        <Card.Meta>Booked on {formatDate(booking.createdAt)}</Card.Meta>

        <Card.Description>
          {booking.status === 'Approved' ? (
            <>
              <div>{booking.applicantName || booking.userName}</div>
              <div style={{ marginTop: 4 }}>
                <Icon name="map marker alternate" />
                <strong>
                  {booking.guestHouseName || booking.guestHousePreferences?.[0] || 'LD Guest House'}
                </strong>
              </div>
            </>
          ) : (
            <strong>{booking.applicantName || booking.userName}</strong>
          )}
        </Card.Description>

        {booking.status === 'Approved' && <RoomAllocationBadge booking={booking} />}

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)' }}>Check-in</div>
            <strong>{formatDate(booking.checkIn)}</strong>
          </div>
          <Icon name="arrow right" />
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)' }}>Check-out</div>
            <strong>{formatDate(booking.checkOut)}</strong>
          </div>
          <Label basic>{nights} night{nights !== 1 ? 's' : ''}</Label>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '0.85rem' }}>
          <span><Icon name="users" />{booking.guests} guest{booking.guests > 1 ? 's' : ''}</span>
          <span><Icon name="briefcase" />{booking.purpose}</span>
          <span><Icon name="credit card outline" />{booking.payment}</span>
          {booking.totalCost && <strong>₹{booking.totalCost.toLocaleString()}</strong>}
        </div>

        {booking.specialRequests && (
          <p style={{ marginTop: 10 }}><Icon name="comment outline" />{booking.specialRequests}</p>
        )}
      </Card.Content>
      <Card.Content extra>
        <Button basic size="small" onClick={() => onViewDetail(booking)}>
          <Icon name="eye" />View Details
        </Button>
        {canCancel && (
          <Button basic negative size="small" onClick={() => onCancel(booking)}>
            <Icon name="ban" />Cancel Booking
          </Button>
        )}
      </Card.Content>
    </Card>
  );
}

function EmptyTab({ label }) {
  return (
    <Segment basic textAlign="center" style={{ padding: '3rem 1rem' }}>
      <Icon name="calendar outline" size="huge" disabled />
      <Header as="h3">No {label} Bookings</Header>
      <p>Your {label.toLowerCase()} bookings will appear here.</p>
    </Segment>
  );
}

function MyBookingsPage() {
  const { currentUser } = useAuth();
  const { bookings, cancelBooking } = useBooking();
  const navigate = useNavigate();

  const [activeTab,   setActiveTab]   = useState('upcoming');
  const [cancelModal, setCancelModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [cancelling,  setCancelling]  = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const myBookings = useMemo(
    () => bookings.filter(b => b.userId === currentUser?.id),
    [bookings, currentUser]
  );

  const upcoming  = useMemo(() => myBookings.filter(b => b.checkOut >= today  && b.status !== 'Cancelled' && b.status !== 'Rejected'), [myBookings, today]);
  const past      = useMemo(() => myBookings.filter(b => b.checkOut <  today  && b.status !== 'Cancelled'), [myBookings, today]);
  const cancelled = useMemo(() => myBookings.filter(b => b.status === 'Cancelled' || b.status === 'Rejected'), [myBookings]);

  const tabs = [
    { key: 'upcoming',  label: 'Upcoming',  count: upcoming.length,  data: upcoming  },
    { key: 'past',      label: 'Past',       count: past.length,      data: past      },
    { key: 'cancelled', label: 'Cancelled',  count: cancelled.length, data: cancelled },
  ];

  const currentData = tabs.find(t => t.key === activeTab)?.data || [];

  const handleCancel = async (booking) => {
    setCancelling(true);
    try {
      await cancelBooking(booking.id);
    } finally {
      setCancelling(false);
      setCancelModal(null);
    }
  };

  const greetingHour = new Date().getHours();
  const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = (currentUser?.fullName || '').split(' ')[0];

  return (
    <Container>
      {/* Hero */}
      <Segment basic vertical>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <Header
            as="h1"
            content={`${greeting}${firstName ? `, ${firstName}` : ''}`}
            subheader="Track and manage all your guest house bookings in one place."
          />
          <Button primary onClick={() => navigate('/rooms')}>
            <Icon name="plus" />Book New Stay
          </Button>
        </div>
      </Segment>

      {/* Stats — the Upcoming/Past/Cancelled cards double as the list filter */}
      <Card.Group itemsPerRow={4} stackable>
        <Card>
          <Card.Content textAlign="center">
            <Icon name="calendar alternate outline" size="large" />
            <Statistic size="mini">
              <Statistic.Value>{myBookings.length}</Statistic.Value>
              <Statistic.Label>Total Bookings</Statistic.Label>
            </Statistic>
          </Card.Content>
        </Card>
        <Card link color={activeTab === 'upcoming' ? 'blue' : undefined} onClick={() => setActiveTab('upcoming')}>
          <Card.Content textAlign="center">
            <Icon name="clock outline" size="large" />
            <Statistic size="mini">
              <Statistic.Value>{upcoming.length}</Statistic.Value>
              <Statistic.Label>Upcoming</Statistic.Label>
            </Statistic>
          </Card.Content>
        </Card>
        <Card link color={activeTab === 'past' ? 'blue' : undefined} onClick={() => setActiveTab('past')}>
          <Card.Content textAlign="center">
            <Icon name="history" size="large" />
            <Statistic size="mini">
              <Statistic.Value>{past.length}</Statistic.Value>
              <Statistic.Label>Past Stays</Statistic.Label>
            </Statistic>
          </Card.Content>
        </Card>
        <Card link color={activeTab === 'cancelled' ? 'blue' : undefined} onClick={() => setActiveTab('cancelled')}>
          <Card.Content textAlign="center">
            <Icon name="ban" size="large" />
            <Statistic size="mini">
              <Statistic.Value>{cancelled.length}</Statistic.Value>
              <Statistic.Label>Cancelled</Statistic.Label>
            </Statistic>
          </Card.Content>
        </Card>
      </Card.Group>

      {/* Content */}
      {currentData.length === 0 ? (
        <EmptyTab label={tabs.find(t => t.key === activeTab)?.label || ''} />
      ) : (
        <Card.Group stackable>
          {currentData.map(b => (
            <BookingCard
              key={b.id}
              booking={b}
              onCancel={setCancelModal}
              onViewDetail={setDetailModal}
            />
          ))}
        </Card.Group>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal open={!!cancelModal} onClose={() => setCancelModal(null)} size="mini" className="bhu-confirm-modal">
        <Modal.Header><Icon name="exclamation triangle" /> Cancel Booking</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to cancel booking <strong>{cancelModal?.bookingCode}</strong>?</p>
          <p className="bhu-confirm-sub">This action cannot be undone.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setCancelModal(null)} disabled={cancelling}>Keep Booking</Button>
          <Button negative loading={cancelling} disabled={cancelling} onClick={() => handleCancel(cancelModal)}>Yes, Cancel</Button>
        </Modal.Actions>
      </Modal>

      {/* Detail Modal */}
      <Modal open={!!detailModal} onClose={() => setDetailModal(null)} size="small" closeIcon className="bhu-detail-modal">
        <Modal.Header><Icon name="calendar check outline" /> Booking Details</Modal.Header>
        <Modal.Content>
          {detailModal && (
            <Table basic="very" compact>
              <Table.Body>
                <DetailRow label="Booking ID"    val={detailModal.bookingCode} />
                <DetailRow label="Status"        val={<StatusLabel status={detailModal.status} />} />
                <DetailRow label="Guest House"   val={detailModal.guestHouseName || detailModal.guestHousePreferences?.[0] || '—'} />
                <DetailRow label="Room"          val={(() => {
                  const assignments = detailModal.roomAssignments?.filter(a => a.roomNumber) || [];
                  if (assignments.length > 0) return assignments.map(a => `Room ${a.roomNumber}${a.roomType ? ' · ' + a.roomType : ''}`).join(', ');
                  if (detailModal.roomNumber) return `Room ${detailModal.roomNumber}${detailModal.roomType ? ' · ' + detailModal.roomType : ''}`;
                  return 'Pending assignment';
                })()} />
                <DetailRow label="Check-in"      val={formatDate(detailModal.checkIn)} />
                <DetailRow label="Check-out"     val={formatDate(detailModal.checkOut)} />
                <DetailRow label="Nights"        val={nightsBetween(detailModal.checkIn, detailModal.checkOut)} />
                <DetailRow label="Guests"        val={detailModal.guests} />
                <DetailRow label="Purpose"       val={detailModal.purpose} />
                <DetailRow label="Payment"       val={detailModal.payment} />
                {detailModal.totalCost && <DetailRow label="Total Cost" val={`₹${detailModal.totalCost.toLocaleString()}`} />}
                {detailModal.specialRequests && <DetailRow label="Special Requests" val={detailModal.specialRequests} />}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
      </Modal>
    </Container>
  );
}

function DetailRow({ label, val }) {
  return (
    <Table.Row>
      <Table.Cell width={6}><strong>{label}</strong></Table.Cell>
      <Table.Cell>{val}</Table.Cell>
    </Table.Row>
  );
}

export default MyBookingsPage;
